import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  BarChart3, Users, TrendingUp, TrendingDown, MessageSquare, 
  BookOpen, Diamond, Lightbulb, Calendar, Activity
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminReportsPage = () => {
  const [reports, setReports] = useState(null);
  const [trends, setTrends] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReports();
    fetchTrends();
  }, []);

  const fetchReports = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/admin/reports/overview`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReports(response.data);
    } catch (error) {
      console.error('Failed to fetch reports:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTrends = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/admin/reports/trends?days=14`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTrends(response.data);
    } catch (error) {
      console.error('Failed to fetch trends:', error);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="p-6 lg:p-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-32 bg-sand rounded-xl animate-pulse" />
            ))}
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8" data-testid="admin-reports-page">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-heading text-3xl lg:text-4xl font-bold text-forest flex items-center gap-3">
            <BarChart3 className="h-8 w-8" />
            Reports & Analytics
          </h1>
          <p className="text-gray-600 mt-2">
            Member engagement, activity trends, and growth insights.
          </p>
        </div>

        {/* Overview Stats */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="card-base">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Members</p>
                  <p className="text-3xl font-bold text-forest">{reports?.overview?.total_members || 0}</p>
                </div>
                <Users className="h-10 w-10 text-forest/30" />
              </div>
            </CardContent>
          </Card>

          <Card className="card-base">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">New (30 days)</p>
                  <p className="text-3xl font-bold text-terracotta">{reports?.overview?.new_members_30d || 0}</p>
                </div>
                <TrendingUp className="h-10 w-10 text-terracotta/30" />
              </div>
            </CardContent>
          </Card>

          <Card className="card-base">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Active (7 days)</p>
                  <p className="text-3xl font-bold text-forest">{reports?.overview?.active_members_7d || 0}</p>
                </div>
                <Activity className="h-10 w-10 text-forest/30" />
              </div>
            </CardContent>
          </Card>

          <Card className="card-base">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Growth Rate</p>
                  <p className={`text-3xl font-bold ${reports?.overview?.growth_rate >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                    {reports?.overview?.growth_rate >= 0 ? '+' : ''}{reports?.overview?.growth_rate || 0}%
                  </p>
                </div>
                {reports?.overview?.growth_rate >= 0 ? (
                  <TrendingUp className="h-10 w-10 text-green-600/30" />
                ) : (
                  <TrendingDown className="h-10 w-10 text-red-500/30" />
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid lg:grid-cols-3 gap-6 mb-8">
          {/* Engagement Metrics */}
          <Card className="card-base lg:col-span-2">
            <CardHeader>
              <CardTitle className="font-heading text-lg text-forest">
                Feature Engagement (Last 30 Days)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="p-4 bg-sand/30 rounded-xl">
                  <div className="flex items-center gap-3">
                    <BookOpen className="h-8 w-8 text-forest" />
                    <div>
                      <p className="text-2xl font-bold text-forest">{reports?.engagement?.journal_entries_30d || 0}</p>
                      <p className="text-sm text-gray-500">Journal Entries</p>
                    </div>
                  </div>
                </div>

                <div className="p-4 bg-sand/30 rounded-xl">
                  <div className="flex items-center gap-3">
                    <MessageSquare className="h-8 w-8 text-terracotta" />
                    <div>
                      <p className="text-2xl font-bold text-terracotta">{reports?.engagement?.comments_30d || 0}</p>
                      <p className="text-sm text-gray-500">Comments</p>
                    </div>
                  </div>
                </div>

                <div className="p-4 bg-sand/30 rounded-xl">
                  <div className="flex items-center gap-3">
                    <MessageSquare className="h-8 w-8 text-purple-600" />
                    <div>
                      <p className="text-2xl font-bold text-purple-600">{reports?.engagement?.messages_30d || 0}</p>
                      <p className="text-sm text-gray-500">Messages</p>
                    </div>
                  </div>
                </div>

                <div className="p-4 bg-sand/30 rounded-xl">
                  <div className="flex items-center gap-3">
                    <Calendar className="h-8 w-8 text-blue-600" />
                    <div>
                      <p className="text-2xl font-bold text-blue-600">{reports?.feature_engagement?.sessions_booked || 0}</p>
                      <p className="text-sm text-gray-500">Sessions Booked</p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Tier Breakdown */}
          <Card className="card-base">
            <CardHeader>
              <CardTitle className="font-heading text-lg text-forest">
                Members by Tier
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {Object.entries(reports?.tier_breakdown || {}).map(([tier, count]) => (
                  <div key={tier} className="flex items-center justify-between">
                    <span className="text-sm capitalize">
                      {tier.replace('_', ' ')}
                    </span>
                    <Badge variant="outline">{count}</Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid lg:grid-cols-2 gap-6 mb-8">
          {/* Most Active Members */}
          <Card className="card-base">
            <CardHeader>
              <CardTitle className="font-heading text-lg text-forest flex items-center gap-2">
                <Diamond className="h-5 w-5 text-purple-600" />
                Most Active Members
              </CardTitle>
            </CardHeader>
            <CardContent>
              {reports?.most_active_members?.length > 0 ? (
                <div className="space-y-3">
                  {reports.most_active_members.map((member, index) => (
                    <div key={member.id} className="flex items-center justify-between p-3 bg-sand/30 rounded-lg">
                      <div className="flex items-center gap-3">
                        <span className="font-bold text-forest">{index + 1}.</span>
                        <span className="font-medium">{member.full_name}</span>
                      </div>
                      <Badge className="bg-purple-100 text-purple-700">
                        <Diamond className="h-3 w-3 mr-1" />
                        {member.diamond_points}
                      </Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-4">No activity data yet</p>
              )}
            </CardContent>
          </Card>

          {/* AI Recommendations */}
          <Card className="card-base">
            <CardHeader>
              <CardTitle className="font-heading text-lg text-forest flex items-center gap-2">
                <Lightbulb className="h-5 w-5 text-yellow-500" />
                Recommendations
              </CardTitle>
            </CardHeader>
            <CardContent>
              {reports?.recommendations?.length > 0 ? (
                <div className="space-y-3">
                  {reports.recommendations.map((rec, index) => (
                    <div key={index} className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <p className="text-sm text-yellow-800">{rec}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-sm text-green-800">
                    Great job! No immediate recommendations. Keep up the engagement!
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Activity Trends */}
        <Card className="card-base">
          <CardHeader>
            <CardTitle className="font-heading text-lg text-forest">
              Activity Trends (Last 14 Days)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {trends.length > 0 ? (
              <div className="overflow-x-auto">
                <div className="flex gap-1 min-w-max">
                  {trends.map((day) => (
                    <div key={day.date} className="flex flex-col items-center w-16">
                      <div className="h-24 w-full bg-gray-100 rounded-t relative overflow-hidden">
                        <div 
                          className="absolute bottom-0 w-full bg-forest transition-all"
                          style={{ height: `${Math.min(day.new_members * 20, 100)}%` }}
                          title={`${day.new_members} new members`}
                        />
                      </div>
                      <div 
                        className="w-full bg-terracotta/30 rounded-b"
                        style={{ height: `${Math.min(day.journal_entries * 5, 30)}px` }}
                        title={`${day.journal_entries} journal entries`}
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        {new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </p>
                    </div>
                  ))}
                </div>
                <div className="flex gap-4 mt-4 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-forest rounded" />
                    <span>New Members</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-terracotta/30 rounded" />
                    <span>Journal Entries</span>
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">No trend data available</p>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default AdminReportsPage;
